import React, { useEffect, useState, useMemo, useCallback } from "react";
import { ref as dbRef, onValue, push, set, update } from "firebase/database";
import { db } from "../firebase";
import "./area.css";

export default function DailyEntryForm({ areaId, date }) {
  const [customers, setCustomers] = useState({});
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [lastActivityMap, setLastActivityMap] = useState({});

  // Fetch customers in selected area
  useEffect(() => {
    if (!areaId) {
      setCustomers({});
      setRows({});
      setLastActivityMap({});
      return;
    }

    const customersRef = dbRef(db, `areas/${areaId}/customers`);
    const unsubscribe = onValue(customersRef, (snap) => {
      const val = snap.val() || {};
      setCustomers(val);
      
      // Initialize rows for ALL customers (preserving Firebase order)
      const initialRows = {};
      Object.entries(val).forEach(([id, c]) => {
        initialRows[id] = {
          out: 0,
          in: 0,
          amountGiven: 0,
          mode: c?.mode || "",
          price: Number(c?.price || 0),
          type: c?.type || "jar",
        };
      });
      setRows(initialRows);
    });
    
    return () => unsubscribe();
  }, [areaId]);

  // Fetch entries to determine last activity per customer
  useEffect(() => {
    if (!areaId) return;

    const entriesRef = dbRef(db, `areas/${areaId}/entries`);
    const unsubscribe = onValue(entriesRef, (snap) => {
      const entriesData = snap.val() || {};
      const activityMap = {};

      // Iterate through all dates and entries
      Object.entries(entriesData).forEach(([entryDate, entriesForDate]) => {
        if (!entriesForDate) return;
        
        Object.values(entriesForDate).forEach((entry) => {
          if (!entry || !entry.customerId) return;
          
          const cid = entry.customerId;
          const hasActivity = 
            (Number(entry.out) || 0) > 0 || 
            (Number(entry.in) || 0) > 0 || 
            (Number(entry.paid) || 0) > 0;
          
          if (!hasActivity) return;

          // Keep the latest date for each customer
          if (!activityMap[cid] || entryDate > activityMap[cid]) {
            activityMap[cid] = entryDate;
          }
        });
      });

      setLastActivityMap(activityMap);
    });

    return () => unsubscribe();
  }, [areaId]);

  const updateRow = useCallback((id, field, value) => {
    setRows(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: parseFloat(value) || 0 }
    }));
  }, []);

  // Calculate totals for ALL customers
  const totals = useMemo(() => {
    let coolOut = 0, coolIn = 0, jarOut = 0, jarIn = 0, totalAmount = 0;
    
    Object.entries(rows).forEach(([id, r]) => {
      const out = Math.max(0, Number(r.out) || 0);
      const inn = Math.max(0, Number(r.in) || 0);
      const amt = Math.max(0, Number(r.amountGiven) || 0);
      
      totalAmount += amt;
      
      const customerType = customers[id]?.type || r.type || "jar";
      const isCoolJar = customerType === "cooljar" || customerType === "cool jar";
      
      if (isCoolJar) {
        coolOut += out;
        coolIn += inn;
      } else {
        jarOut += out;
        jarIn += inn;
      }
    });
    
    return { coolOut, coolIn, jarOut, jarIn, totalAmount };
  }, [rows, customers]);

  // Calculate pending amounts for a customer
  const calculatePending = useCallback((customer, currentOut, currentIn, currentPaid) => {
    const prevPendingJars = Math.max(0, Number(customer.pendingJars) || 0);
    const prevPendingAmount = Math.max(0, Number(customer.pendingAmount) || 0);
    const prevAdvance = Math.max(0, Number(customer.advance) || 0);
    const price = Number(customer.price) || 0;
    
    const netJarsChange = currentOut - currentIn;
    const newPendingJars = Math.max(0, prevPendingJars + netJarsChange);
    
    const expectedAmount = currentOut * price;
    const amountDifference = expectedAmount - currentPaid;
    
    let newPendingAmount = prevPendingAmount;
    let newAdvance = prevAdvance;
    
    if (amountDifference > 0) {
      newPendingAmount += amountDifference;
    } else if (amountDifference < 0) {
      newAdvance += Math.abs(amountDifference);
    }
    
    const balanceAmount = Math.min(newAdvance, newPendingAmount);
    newAdvance -= balanceAmount;
    newPendingAmount -= balanceAmount;
    
    return {
      pendingJars: newPendingJars,
      pendingAmount: newPendingAmount,
      advance: newAdvance
    };
  }, []);

  // Submit entries - only save customers with activity
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!areaId) {
      setMessage("❌ Area not selected.");
      return;
    }
    
    const activeEntries = Object.entries(rows).filter(([id, row]) => {
      const out = Math.max(0, Number(row.out) || 0);
      const inn = Math.max(0, Number(row.in) || 0);
      const paid = Math.max(0, Number(row.amountGiven) || 0);
      return out > 0 || inn > 0 || paid > 0;
    });
    
    if (activeEntries.length === 0) {
      setMessage("⚠️ No data to save. Please enter some values.");
      return;
    }
    
    setSaving(true);
    setMessage("");

    try {
      const entryPath = `areas/${areaId}/entries/${date}`;
      const updates = [];
      const customerUpdates = {};

      for (const [id, row] of activeEntries) {
        const out = Math.max(0, Number(row.out) || 0);
        const inn = Math.max(0, Number(row.in) || 0);
        const paid = Math.max(0, Number(row.amountGiven) || 0);
        
        const customer = customers[id];
        if (!customer) continue;
        
        const entryRef = push(dbRef(db, entryPath));
        updates.push(set(entryRef, {
          customerId: id,
          customerName: customer.name,
          out,
          in: inn,
          paid,
          price: Number(row.price) || 0,
          mode: row.mode || "daily",
          timestamp: Date.now(),
          date: date
        }));
        
        const newPending = calculatePending(customer, out, inn, paid);
        
        customerUpdates[`areas/${areaId}/customers/${id}`] = {
          pendingJars: newPending.pendingJars,
          pendingAmount: newPending.pendingAmount,
          advance: newPending.advance,
          lastUpdated: Date.now()
        };
      }
      
      if (updates.length > 0) {
        const updatePromises = [...updates];
        
        for (const [path, data] of Object.entries(customerUpdates)) {
          updatePromises.push(update(dbRef(db, path), data));
        }
        
        await Promise.all(updatePromises);
        setMessage(`✅ Successfully saved ${updates.length} entries.`);
        
        setTimeout(() => {
          const resetRows = { ...rows };
          activeEntries.forEach(([id]) => {
            resetRows[id] = {
              ...resetRows[id],
              out: 0,
              in: 0,
              amountGiven: 0,
            };
          });
          setRows(resetRows);
          setMessage("");
        }, 2000);
      }
      
    } catch (err) {
      console.error("Save error:", err);
      setMessage("❌ Error saving entries. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getPendingInfo = useCallback((customerId) => {
    const customer = customers[customerId];
    if (!customer) return null;
    
    const pendingJars = Math.max(0, Number(customer.pendingJars) || 0);
    const pendingAmount = Math.max(0, Number(customer.pendingAmount) || 0);
    const advance = Math.max(0, Number(customer.advance) || 0);
    
    if (pendingJars === 0 && pendingAmount === 0 && advance === 0) return null;
    
    return { pendingJars, pendingAmount, advance };
  }, [customers]);

  // Calculate days since last activity for a customer
  const getDaysSinceLastActivity = useCallback((customerId) => {
    const lastDate = lastActivityMap[customerId];
    if (!lastDate) return null; // No activity ever recorded

    const parseDate = (d) => {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day);
    };

    const last = parseDate(lastDate);
    const today = parseDate(date);
    
    const diffMs = today - last;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }, [lastActivityMap, date]);

  // Format the "days ago" display
  const formatDaysAgo = (days) => {
    if (days === null) return null;
    if (days <= 0) return null; // Activity today
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  return (
    <div className="daily-entry-container">
      <form onSubmit={handleSubmit} className="daily-entry-form">
        {/* Summary Section */}
        <div className="summary-section">
          <div className="summary-card">
            <label>Cool Jar Out</label>
            <input readOnly value={totals.coolOut} className="summary-input" />
          </div>
          <div className="summary-card">
            <label>Cool Jar In</label>
            <input readOnly value={totals.coolIn} className="summary-input" />
          </div>
          <div className="summary-card">
            <label>Jar Out</label>
            <input readOnly value={totals.jarOut} className="summary-input" />
          </div>
          <div className="summary-card">
            <label>Jar In</label>
            <input readOnly value={totals.jarIn} className="summary-input" />
          </div>
          <div className="summary-card">
            <label>Total Amount</label>
            <input readOnly value={`₹${totals.totalAmount}`} className="summary-input highlight" />
          </div>
          <div className="summary-card">
            <label>Date</label>
            <input readOnly value={date} className="summary-input" />
          </div>
          <div className="summary-card">
            <label>Total Customers</label>
            <input readOnly value={Object.keys(customers).length} className="summary-input" />
          </div>
        </div>

        {/* Header */}
        <div className="table-header">
          <div>Customer Name</div>
          <div>Out (Jars)</div>
          <div>In (Jars)</div>
          <div>Mode</div>
          <div>Price (₹)</div>
          <div>Amount Given (₹)</div>
        </div>

        {/* ALL Customers List */}
        <div className="customer-list">
          {Object.keys(customers).length === 0 ? (
            <div className="no-customers">
              <p>👥 No customers found in this area.</p>
              <small>Please add customers first.</small>
            </div>
          ) : (
            Object.entries(customers).map(([id, customer]) => {
              const row = rows[id] || {
                out: 0,
                in: 0,
                amountGiven: 0,
                mode: customer.mode || "",
                price: Number(customer.price || 0),
                type: customer.type || "jar",
              };
              const pendingInfo = getPendingInfo(id);
              const hasActivity = (row.out > 0) || (row.in > 0) || (row.amountGiven > 0);
              const daysSince = getDaysSinceLastActivity(id);
              const daysLabel = formatDaysAgo(daysSince);
              
              return (
                <div key={id} className={`customer-card ${hasActivity ? 'has-activity' : ''}`}>
                  <div className="customer-grid">
                    <div className="customer-name">
                      {customer.name}
                      {customer.type === "cooljar" && <span className="cool-badge">❄️ Cool</span>}
                      {daysLabel && (
                        <span className={`inactive-badge ${daysSince >= 2 ? 'warning' : ''}`}>
                          🕒 {daysLabel}
                        </span>
                      )}
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.out || 0}
                      onChange={(e) => updateRow(id, "out", e.target.value)}
                      className="entry-input"
                      placeholder="0"
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.in || 0}
                      onChange={(e) => updateRow(id, "in", e.target.value)}
                      className="entry-input"
                      placeholder="0"
                    />
                    <input readOnly value={row.mode || customer.mode} className="readonly-input" />
                    <input readOnly value={`₹${row.price || customer.price}`} className="readonly-input" />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.amountGiven || 0}
                      onChange={(e) => updateRow(id, "amountGiven", e.target.value)}
                      className="entry-input"
                      placeholder="0"
                    />
                  </div>
                  
                  {pendingInfo && (
                    <div className="pending-info">
                      {pendingInfo.pendingJars > 0 && (
                        <span className="badge pending-jars">📦 Pending Jars: {pendingInfo.pendingJars}</span>
                      )}
                      {pendingInfo.pendingAmount > 0 && (
                        <span className="badge pending-amount">💰 Pending Amount: ₹{pendingInfo.pendingAmount}</span>
                      )}
                      {pendingInfo.advance > 0 && (
                        <span className="badge advance">✅ Advance: ₹{pendingInfo.advance}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {Object.keys(customers).length > 0 && (
          <div className="form-footer">
            <button type="submit" disabled={saving} className="submit-btn">
              {saving ? "💾 Saving..." : "📝 Submit All Entries"}
            </button>
            <button 
              type="button" 
              onClick={() => {
                const resetRows = { ...rows };
                Object.keys(resetRows).forEach(id => {
                  resetRows[id] = {
                    ...resetRows[id],
                    out: 0,
                    in: 0,
                    amountGiven: 0,
                  };
                });
                setRows(resetRows);
                setMessage("🔄 Form reset successfully.");
                setTimeout(() => setMessage(""), 2000);
              }}
              className="reset-btn"
              disabled={saving}
            >
              🔄 Reset Form
            </button>
          </div>
        )}
        
        {message && <div className={`status-message ${message.includes('✅') ? 'success' : message.includes('🔄') ? 'info' : 'error'}`}>{message}</div>}
      </form>

      <style jsx>{`
        .daily-entry-container {
          display: flex;
          justify-content: center;
          padding: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
        }

        .daily-entry-form {
          width: 100%;
          max-width: 1200px;
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .summary-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: 12px;
          margin-bottom: 24px;
        }

        .summary-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 12px;
          border-radius: 10px;
          text-align: center;
          color: white;
        }

        .summary-card label {
          font-size: 12px;
          opacity: 0.9;
          display: block;
          margin-bottom: 5px;
        }

        .summary-input {
          width: 100%;
          padding: 6px;
          font-size: 16px;
          font-weight: bold;
          text-align: center;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 6px;
          color: white;
        }

        .summary-input.highlight {
          background: rgba(255,255,255,0.3);
          font-size: 18px;
        }

        .table-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
          background: #f0f0f0;
          padding: 12px;
          border-radius: 8px;
          font-weight: bold;
          margin-bottom: 12px;
          text-align: center;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .customer-list {
          max-height: 60vh;
          overflow-y: auto;
          margin-bottom: 20px;
        }

        .customer-card {
          background: #fafafa;
          margin-bottom: 12px;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          transition: all 0.2s;
        }

        .customer-card.has-activity {
          background: #fffbf0;
          border-color: #ffc107;
        }

        .customer-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .customer-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
          gap: 8px;
          align-items: center;
        }

        .customer-name {
          font-weight: 600;
          color: #333;
          padding: 0 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .cool-badge {
          font-size: 11px;
          background: #00b4d8;
          color: white;
          padding: 2px 6px;
          border-radius: 12px;
        }

        .inactive-badge {
          font-size: 11px;
          background: #e2e3e5;
          color: #495057;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }

        .inactive-badge.warning {
          background: #f8d7da;
          color: #721c24;
          font-weight: 600;
        }

        .entry-input, .readonly-input {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          text-align: center;
        }

        .entry-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102,126,234,0.1);
        }

        .readonly-input {
          background: #f5f5f5;
          color: #666;
        }

        .pending-info {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px dashed #ddd;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .badge {
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .pending-jars {
          background: #fff3cd;
          color: #856404;
        }

        .pending-amount {
          background: #f8d7da;
          color: #721c24;
        }

        .advance {
          background: #d4edda;
          color: #155724;
        }

        .form-footer {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .submit-btn, .reset-btn {
          flex: 1;
          padding: 12px;
          font-size: 16px;
          font-weight: bold;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .submit-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .reset-btn {
          background: #6c757d;
          color: white;
        }

        .submit-btn:hover:not(:disabled), .reset-btn:hover:not(:disabled) {
          transform: translateY(-2px);
        }

        .submit-btn:disabled, .reset-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-message {
          text-align: center;
          margin-top: 12px;
          padding: 10px;
          border-radius: 8px;
          font-weight: 500;
        }

        .status-message.success {
          background: #d4edda;
          color: #155724;
        }

        .status-message.error {
          background: #f8d7da;
          color: #721c24;
        }

        .status-message.info {
          background: #d1ecf1;
          color: #0c5460;
        }

        .no-customers {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .no-customers p {
          font-size: 18px;
          margin-bottom: 8px;
        }

        .no-customers small {
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .daily-entry-form {
            padding: 16px;
          }
          
          .table-header {
            display: none;
          }
          
          .customer-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          
          .customer-name {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            padding: 8px;
            background: #f0f0f0;
            border-radius: 6px;
            justify-content: center;
          }
          
          .entry-input, .readonly-input {
            text-align: left;
          }
          
          .form-footer {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}